// Fordham University: public college page (#2840), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Fordham's Common Data Set 2024-2025 ("FORDHAM UNIVERSITY CDS_2024-2025",
//    an Excel workbook), the newest edition the Office of Institutional
//    Research and Assessment links from its Common Data Set page. The file
//    sits on Fordham's SharePoint as view-only (download and print are
//    switched off), so it was read in Excel for the web, sheet by sheet; the
//    SOURCES url is the fordham.edu page that links it. No 2025-2026 edition
//    was posted on 2026-10-01, so the counts are for the Fall 2024 class.
//    C1 applicants/admits/enrollees with the residency table; C8 test policy
//    (the edition asks about fall 2026 applicants); C21/C22 early plans with
//    early decision counts. The CDS's G1 costs are for 2025-2026, so cost
//    comes from Student Financial Services instead (below).
//  - fordham.edu Undergraduate Admission: How to Apply (the 2026-2027
//    deadline table with notification dates, the plan definitions and the
//    dance, theatre and visual arts admission steps), Early Decision Benefits
//    (binding terms, two ED rounds) and Testing Policy (test-optional; the
//    page carries no year, and the How to Apply page for this cycle lists
//    SAT or ACT scores under optional components).
//  - Student Financial Services, Fordham College at Rose Hill cost of
//    attendance, 2026-2027, "Fall 2026 Entrants", resident column. Fordham
//    posts one table per college; the Rose Hill table is identical to the
//    Gabelli School's. Lincoln Center residents pay more for food and housing
//    ($26,810, total $103,041, read the same day from the Fordham College at
//    Lincoln Center table); the cost note says only that it costs more.
//  - College Scorecard, school id 191241 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Fordham Common Data Set 2024-2025',
        url: 'https://www.fordham.edu/about/leadership-and-administration/administrative-offices/office-of-the-provost/provost-office-units/institutional-research-and-assessment/consumer-information/common-data-set/',
        read: '2026-10-01',
    },
    apply: {
        label: 'Fordham Undergraduate Admission, How to Apply',
        url: 'https://www.fordham.edu/undergraduate-admission/apply/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Fordham Undergraduate Admission, Early Decision Benefits at Fordham',
        url: 'https://www.fordham.edu/undergraduate-admission/apply/early-decision-benefits-at-fordham/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Fordham Undergraduate Admission, Testing Policy',
        url: 'https://www.fordham.edu/undergraduate-admission/apply/what-were-looking-for/testing-policy/',
        read: '2026-10-01',
    },
    costRoseHill: {
        label: 'Fordham Student Financial Services, Fordham College at Rose Hill Tuition and Fees',
        url: 'https://www.fordham.edu/student-financial-services/tuition-and-payments/undergraduate-tuition/fordham-college-at-rose-hill/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?191241-Fordham-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'fordham-university',
    kbId: 'fordham_university',
    name: 'Fordham University',
    shortName: 'Fordham',
    location: 'Bronx, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (fordham.edu: Fordham Maroon, White); decoration only.
    branding: {
        primary: '#72253D',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/fordham_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 43364,
        admitted: 25719,
        enrolled: 2492,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 12785, admitted: 7592 },
            { group: 'Out-of-state', applied: 25123, admitted: 16058 },
            { group: 'International', applied: 5456, admitted: 2069 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Fordham\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college, campus or major. The newest edition posted is 2024-2025, so these counts are for the fall 2024 class.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'Early decision applicants and admits for the fall 2024 class, as the 2024-2025 Common Data Set counts them. It gives no early action counts, so there is no early action rate.',
        year: 'Fall 2024',
        rows: [
            { plan: 'Early Decision', applied: 595, admitted: 307 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You choose a campus (Rose Hill in the Bronx or Lincoln Center in Manhattan) and a college when you apply, and you are evaluated for the ones you choose. Three majors ask for more:',
        rows: [
            { school: 'Dance (Ailey/Fordham BFA)', requirement: 'Apply by the Early Action deadline on both the Common Application and the Ailey Application. Selected candidates are invited to audition. You also name an alternate major, which you are considered for if not admitted to dance. Decisions come on a rolling basis between February 1 and April 1.' },
            { school: 'Theatre', requirement: 'Apply by the Early Action deadline. Every program in the major needs a creative review: a prescreen, then a live audition (Performance) or an interview (Design and Production, Directing, Playwriting). You also name an alternate major. Decisions come on a rolling basis between February 1 and April 1.' },
            { school: 'Visual Arts', requirement: 'Any plan works. Fordham recommends a portfolio of up to 10 samples of your work or up to three short videos, uploaded through the applicant portal after you apply.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 3' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'December 20', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 20', binding: true },
            { plan: 'Regular Decision', deadline: 'January 3', decision: 'April 1', binding: false },
            { plan: 'Early Decision II', deadline: 'January 3', decision: 'February 15', binding: true },
        ],
        note: 'All application materials are due November 20 (Early Action, Early Decision I) or January 20 (Regular Decision, Early Decision II); completing by then guarantees full consideration for admission and scholarships. Deposits are due May 1 (Early Action, Regular Decision), January 15 (Early Decision I) and March 15 (Early Decision II).',
        source: 'apply',
        binding: 'Early Decision is binding: if admitted, you must enroll at Fordham and withdraw any other college applications you submitted. There are two rounds, ED I and ED II. Before you commit, US citizens and eligible noncitizens can ask for an optional early financial aid review, with an estimated award starting in October.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Fordham does not require SAT or ACT scores. On the Common Application you choose whether Fordham considers your scores; if you ask for them to be reviewed, your application is incomplete until they arrive. You may report your own scores in the applicant portal, and Fordham superscores. Test scores are not needed for merit scholarships or honors programs.',
        source: 'tests',
        cdsLine: 'The 2024-2025 Common Data Set marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2026 applicants, with scores due by February 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 68886 },
            { item: 'Required fees', amount: 2063 },
            { item: 'Food and housing (on campus)', amount: 25805 },
            { item: 'Books and supplies', amount: 1692 },
            { item: 'Transportation', amount: 1327 },
            { item: 'Other expenses', amount: 2415 },
        ],
        total: 102188,
        note: 'Estimated cost for a first-year student living on campus at Rose Hill (Fordham College at Rose Hill or the Gabelli School of Business), as Fordham lists it for fall 2026 entrants. Fordham posts a separate table for Lincoln Center, where on-campus food and housing costs more.',
        source: 'costRoseHill',
        where: 'Estimated Cost of Attendance, Fall 2026 Entrants, Resident',
        netPrice: {
            average: 42760,
            byIncome: [
                { income: '$0 to $30,000', amount: 32069 },
                { income: '$30,001 to $48,000', amount: 30300 },
                { income: '$48,001 to $75,000', amount: 35706 },
                { income: '$75,001 to $110,000', amount: 40101 },
                { income: 'Over $110,000', amount: 51995 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Fordham\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
